'use client';
import React, { useState } from 'react';
import { Loader2, Zap, AlertCircle } from 'lucide-react';

interface PayNowCheckoutProps {
  amount: number;
  itemName: string;
  itemDescription?: string;
  paymentId: string;
  email?: string;
  onCancel?: () => void;
  buttonLabel?: string;
  className?: string;
}

export default function PayNowCheckout({
  amount,
  itemName,
  itemDescription,
  paymentId,
  email,
  onCancel,
  buttonLabel = 'Pay with PayNow',
  className = '',
}: PayNowCheckoutProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePayment() {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/paynow/create-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount.toFixed(2),
          itemName,
          itemDescription,
          paymentId,
          email,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to initiate payment');
      }

      window.location.href = data.paymentLink;
    } catch (err: any) {
      setError(err.message || 'Payment initiation failed. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-xl">
          <AlertCircle size={15} className="text-danger shrink-0 mt-0.5" />
          <p className="text-xs text-danger">{error}</p>
        </div>
      )}
      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handlePayment}
          disabled={loading}
          className={`flex items-center justify-center gap-2 py-3 px-5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed ${onCancel ? 'flex-1' : 'w-full'} ${className}`}
        >
          {loading ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Redirecting to PayNow...
            </>
          ) : (
            <>
              <Zap size={15} />
              {buttonLabel}
            </>
          )}
        </button>
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Secured by{' '}
        <span className="font-semibold text-foreground">PayNow Zimbabwe</span> · ZWL · EcoCash · ZIPIT
      </p>
    </div>
  );
}
